import {
  Accordion,
  Badge,
  BoxV2 as Box,
  ButtonV2 as Button,
  DrawerItem,
  Flex,
  MessageBar,
  RoleGuard,
  Tabs,
  useDrawerContext,
  usePermission,
} from 'portal-commons';
import {
  FunctionComponent,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  matchPath,
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom';
import { faBuilding, faPen } from '@fortawesome/pro-regular-svg-icons';
import queryString from 'query-string';

import {
  Authplus2faForm,
  Authplus2faTab,
  CompanyDetailsTab,
  IdentityStatusForm,
  IdentityStatusTab,
  SenderIdForm,
  SenderIdTab,
  TrademarkVerificationForm,
  TrademarkVerificationTab,
  BrandDetailsForm,
} from '../../components/BrandDetailsV2';
import { BrandDetailsFormValues } from '../../components/BrandRegistrationV2/types';
import {
  FormModal,
  InfoRow,
  TabPanel,
} from '../../components/BrandDetailsV2/components';
import { BrandStatus } from '../../types';
import { Brand, brandController, SenderId, Trademark } from '@/apis/core';
import { BRAND_DETAILS_PATH, BRANDS_DEFAULT_ENTRY } from '@/constants/paths';
import { PageContainer } from '@/components';
import { Loader } from '@/shared_elements';
import { toastFlashMessage } from '@/utils';
import { humanize } from '@/utils/string';
import { convertTimeWithTimezone } from '@/utils/time';
import { getBadgeTypeByStatus } from '@/applications/CspPortal/Brands/utils';

interface BrandDetailsProps {
  brandUid?: string;
  onDeleteSuccess?: () => void;
}

interface LocationState {
  created?: boolean;
}

enum ModalType {
  None = 'none',
  IdentityStatus = 'identityStatus',
  Authplus2fa = 'authplus2fa',
  TrademarkVerification = 'trademarkVerification',
  SenderId = 'senderId',
  CompanyDetails = 'companyDetails',
}

const tabs = [
  { label: 'Identity Status', id: 'identityStatus' },
  { label: 'Company Details', id: 'companyDetails' },
  { label: 'Auth+ 2FA', id: 'authplus2fa' },
  { label: 'Trademark Verification', id: 'trademarkVerification' },
  { label: 'Sender ID', id: 'senderId' },
];

type TabName =
  | 'identityStatus'
  | 'companyDetails'
  | 'authplus2fa'
  | 'trademarkVerification'
  | 'senderId';

const BrandDetails: FunctionComponent<BrandDetailsProps> = ({
  brandUid: propsBrandUid,
  onDeleteSuccess,
}) => {
  const { id: brandUidFromParams } = useParams<{ id: string }>();
  const brandUid = propsBrandUid || brandUidFromParams || '';
  const { pathname, search, state } = useLocation<LocationState>();
  const history = useHistory();
  const { hasPermission } = usePermission();

  const { stack, push, pop } = matchPath(pathname, BRAND_DETAILS_PATH)
    ? {
        stack: undefined,
        push: (type: ModalType) => setModalType(type),
        pop: () => setModalType(ModalType.None),
      }
    : useDrawerContext();
  const insideDrawer = !!stack;

  const queryParams = useMemo(() => queryString.parse(search), [search]);

  const [brand, setBrand] = useState<Brand>();
  const [loading, setLoading] = useState(true);
  const [noticeVisible, setNoticeVisible] = useState(!!state?.created);
  const [activeTab, setActiveTab] = useState<TabName>(() => {
    if (!insideDrawer && queryParams.tab) {
      const tab = queryParams.tab as string;
      if (tabs.some((t) => t.id === tab)) {
        return tab as TabName;
      }
    }
    return 'identityStatus';
  });
  const [identityStatusReloadKey, setIdentityStatusReloadKey] = useState(
    Date.now()
  );
  const [authplus2faReloadKey, setAuthplus2faReloadKey] = useState(Date.now());
  const [trademarkVerificationReloadKey, setTrademarkVerificationReloadKey] =
    useState(Date.now());
  const [senderIdReloadKey, setSenderIdReloadKey] = useState(Date.now());
  const [modalType, setModalType] = useState(ModalType.None);
  const [maxSenderIdCount, setMaxSenderIdCount] = useState<number>();
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [trademarks, setTrademarks] = useState<Trademark[]>([]);
  const [editingSenderId, setEditingSenderId] = useState<SenderId>();
  const isDeleted = brand?.status === BrandStatus.Deleted;
  const activeModalType =
    insideDrawer && stack ? stack[stack.length - 1] : modalType;

  const handleTabChange = (tab: TabName) => {
    setActiveTab(tab);
    if (!insideDrawer) {
      const newSearch = queryString.stringify({ ...queryParams, tab });
      history.push({ pathname, search: newSearch, state });
    }
  };

  useEffect(() => {
    if (!insideDrawer && queryParams.tab && queryParams.tab !== activeTab) {
      const tab = queryParams.tab as string;
      if (tabs.some((t) => t.id === tab)) {
        setActiveTab(tab as TabName);
      }
    }
  }, [queryParams.tab, insideDrawer]);

  useEffect(() => {
    /**
     * Workaround to fix below issue in drawer
     * 1. Edit an existing Sender ID.
     * 2. Click the back arrow (ArrowLeft icon) in the top-left corner of the Drawer.
     * 3. Click the "Add Sender ID" button.
     * result: The Drawer reopens with the previous edit data instead of resetting to a fresh form.
     */
    if (activeModalType !== ModalType.SenderId) {
      setEditingSenderId(undefined);
    }
  }, [activeModalType]);

  const fetchBrand = async (uid: string) => {
    setLoading(true);
    const data = await brandController.getBrand(uid);
    if (data) {
      setBrand(data);
      setLoading(false);
    }
  };

  const handleResubmit = async () => {
    const response = await brandController.revet(brandUid as string);
    if (response) {
      toastFlashMessage('Brand successfully resubmitted.', 'success');
    }
  };

  const handleDeleteBrand = async () => {
    setDeleting(true);
    const response = await brandController.deleteBrand(brandUid);
    setDeleteConfirm(false);
    if (response) {
      toastFlashMessage(`Brand deleted successfully`, 'success');
      if (insideDrawer) {
        pop();
        onDeleteSuccess?.();
      } else {
        history.push(BRANDS_DEFAULT_ENTRY);
      }
    } else {
      setDeleting(false);
      toastFlashMessage(
        `There was a problem deleting brand. Please try again later.`,
        'error'
      );
    }
  };

  const handleEditSubmit = async (
    data: BrandDetailsFormValues,
    isResubmit: boolean
  ) => {
    setUpdating(true);
    const response = await brandController.updateBrand(brandUid, data);

    if (!response) {
      setUpdating(false);
      return;
    }

    let resubmitSuccess = true;
    if (isResubmit) {
      const revetResponse = await brandController.revet(brandUid);
      resubmitSuccess = !!revetResponse;
    }

    setUpdating(false);
    setBrand(response);
    setIdentityStatusReloadKey(Date.now());

    if (!resubmitSuccess) {
      return;
    }

    if (isResubmit) {
      toastFlashMessage(
        'Brand Details have been successfully updated & resubmitted for verification.',
        'success'
      );
    } else {
      toastFlashMessage('Brand details updated successfully.', 'success');
    }
    pop();
  };

  const renderContent = () =>
    loading ? (
      <Loader hocFlag />
    ) : brand ? (
      <PageContainer
        sx={{
          gap: insideDrawer ? '16px' : '24px',
          padding: 0,
          lineHeight: 'normal',
          ...(insideDrawer
            ? {}
            : {
                padding: '10px 66px 20px 71px',
              }),
        }}
      >
        {!insideDrawer && (
          <Flex
            sx={{
              alignItems: 'center',
              gap: 'xs',
              height: '32px',
              mb: '16px',
              color: 't.black70',
              fontWeight: 400,
              fontSize: 'H400',
            }}
          >
            <Box>Brands</Box>
            <Box>/</Box>
            <Box as="span" sx={{ color: 't.black60', fontWeight: 600 }}>
              {brandUid}
            </Box>
          </Flex>
        )}
        <Flex
          sx={{
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box as="section" sx={{ width: '100%' }}>
            <Box
              sx={{ fontSize: '24px', color: '#19262a', mb: '8px' }}
              data-testid="companyName"
            >
              Brand Legal Name:{' '}
              <Box as="span" sx={{ fontWeight: 'bold', mx: '8px' }}>
                {brand.companyName}
              </Box>
              <Button
                type="button"
                size="lg"
                variant="transparent"
                startAdornment={faPen}
                color="primary"
                shape="square"
                onClick={() => push(ModalType.CompanyDetails)}
                disabled={
                  !hasPermission('brandDetail.editCompanyDetails') || isDeleted
                }
                data-testid="editBrandBtn"
              >
                Edit
              </Button>
            </Box>
            <Box
              sx={{ fontSize: '14px', color: '#4d5659', mb: '8px' }}
              data-testid="displayName"
            >
              Brand DBA Name:{' '}
              <Box as="span" sx={{ fontWeight: '600' }}>
                {brand.displayName}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: '16px' }}>
              <Badge
                size="lg"
                variant={insideDrawer ? 'fill' : 'outline'}
                title="Identity Status"
                type={getBadgeTypeByStatus(brand.identityStatus || 'pending')}
                data-testid="identityStatus"
              >
                {humanize(brand.identityStatus || 'pending')}
              </Badge>
              <Badge
                size="lg"
                variant={insideDrawer ? 'fill' : 'outline'}
                title="LOA Status"
                type={getBadgeTypeByStatus(brand.loaStatus)}
                data-testid="loaStatus"
              >
                {humanize(brand.loaStatus) || 'N/A'}
              </Badge>
            </Box>
          </Box>
          {deleteConfirm ? (
            <MessageBar
              type="error"
              title="Delete this brand?"
              showClose={false}
              primaryAction={{
                label: 'Confirm Delete',
                onClick: handleDeleteBrand,
              }}
              secondaryAction={{
                label: 'Cancel',
                onClick: () => setDeleteConfirm(false),
              }}
              sx={{ width: 370 }}
              data-testid="deleteBrandConfirmMessageBar"
            >
              This action is irrevocable.
            </MessageBar>
          ) : (
            <RoleGuard feature="brandDetail.viewMoreActions">
              <Button
                shape="round"
                color="secondary"
                variant="outline"
                size="lg"
                disabled={deleting || isDeleted}
                onClick={() => setDeleteConfirm(true)}
                data-testid="deleteBrandBtn"
              >
                Delete Brand
              </Button>
            </RoleGuard>
          )}
        </Flex>

        <Accordion
          title="Company Overview"
          icon={faBuilding}
          sx={{ width: '100%', mt: insideDrawer ? '8px' : 0 }}
          defaultExpanded
          data-testid="companyOverview"
        >
          <InfoRow col={insideDrawer ? 3 : 4}>
            <InfoRow.Item label="Brand ID" testId="uid">
              {brand.uid}
            </InfoRow.Item>
            <InfoRow.Item label="Registered" testId="createDate">
              {convertTimeWithTimezone(
                brand.createDate,
                undefined,
                'MM/DD/YYYY'
              )}
            </InfoRow.Item>
            <InfoRow.Item label="Entity Type" testId="entityType">
              {humanize(brand.entityType)}
            </InfoRow.Item>
            <InfoRow.Item label="Company registration number / ID" testId="ein">
              {brand.ein}
            </InfoRow.Item>
          </InfoRow>
        </Accordion>

        {noticeVisible && (
          <MessageBar
            type="info"
            title="Important information about your Brand"
            onClose={() => setNoticeVisible(false)}
          >
            To generate a Letter of Authorization, this brand must have:
            verified identity, verified Auth+ 2FA, at least 1 verified
            trademark, and at least 1 Sender ID with all required details
            entered.
          </MessageBar>
        )}

        <Box
          sx={{
            border: '1px solid #cccfd0',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            paddingX: '16px',
            paddingY: '24px',
            position: 'relative',
            width: '100%',
          }}
        >
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(tab) => handleTabChange(tab as TabName)}
            sx={{ mb: '24px' }}
            data-testid="brandDetailTabs"
          />
          <TabPanel active={activeTab === 'identityStatus'}>
            <IdentityStatusTab
              key={identityStatusReloadKey}
              readOnly={isDeleted}
              brandUid={brandUid as string}
              onAppeal={() => push(ModalType.IdentityStatus)}
              onResubmit={handleResubmit}
            />
          </TabPanel>
          <TabPanel active={activeTab === 'companyDetails'}>
            <CompanyDetailsTab
              brand={brand}
              editable={!isDeleted}
              onEdit={() => push(ModalType.CompanyDetails)}
            />
          </TabPanel>
          <TabPanel active={activeTab === 'authplus2fa'}>
            <Authplus2faTab
              key={authplus2faReloadKey}
              readOnly={isDeleted}
              brandUid={brandUid as string}
              onApply={() => push(ModalType.Authplus2fa)}
            />
          </TabPanel>
          <TabPanel active={activeTab === 'trademarkVerification'}>
            <TrademarkVerificationTab
              key={trademarkVerificationReloadKey}
              readOnly={isDeleted}
              brandUid={brandUid as string}
              onApply={() => push(ModalType.TrademarkVerification)}
              onDataChange={setTrademarks}
            />
          </TabPanel>
          <TabPanel active={activeTab === 'senderId'}>
            <SenderIdTab
              key={senderIdReloadKey}
              readOnly={isDeleted}
              brandUid={brandUid as string}
              brand={brand}
              trademarks={trademarks}
              onSenderIdAdd={(maxCount) => {
                setMaxSenderIdCount(maxCount);
                push(ModalType.SenderId);
              }}
              onEditClick={(senderId) => {
                setEditingSenderId(senderId);
                push(ModalType.SenderId);
              }}
              reloadTrademarks={() =>
                setTrademarkVerificationReloadKey(Date.now())
              }
            />
          </TabPanel>
        </Box>
      </PageContainer>
    ) : null;

  const renderForm = (type: ModalType, node: ReactNode) =>
    insideDrawer ? (
      <DrawerItem id={type}>{node}</DrawerItem>
    ) : (
      <FormModal open={modalType === type} onClose={pop}>
        {modalType === type ? node : null}
      </FormModal>
    );

  useEffect(() => {
    if (brandUid) {
      fetchBrand(brandUid);
    }
    return () => {
      setBrand(undefined);
      setLoading(true);
      setActiveTab('identityStatus');
    };
  }, [brandUid]);

  return (
    <>
      {insideDrawer ? (
        <DrawerItem id="brandDetails">{renderContent()}</DrawerItem>
      ) : (
        renderContent()
      )}
      {renderForm(
        ModalType.IdentityStatus,
        <IdentityStatusForm
          brand={brand}
          onCancel={pop}
          onSubmit={() => {
            setIdentityStatusReloadKey(Date.now());
            pop();
          }}
        />
      )}
      {renderForm(
        ModalType.Authplus2fa,
        <Authplus2faForm
          brandUid={brandUid as string}
          onCancel={pop}
          onSubmit={() => {
            setAuthplus2faReloadKey(Date.now());
            pop();
          }}
        />
      )}
      {renderForm(
        ModalType.TrademarkVerification,
        <TrademarkVerificationForm
          brandUid={brandUid as string}
          onCancel={pop}
          onSubmit={() => {
            setTrademarkVerificationReloadKey(Date.now());
            pop();
          }}
        />
      )}
      {renderForm(
        ModalType.SenderId,
        <SenderIdForm
          brandUid={brandUid as string}
          senderId={editingSenderId}
          trademarks={trademarks}
          maxCount={maxSenderIdCount}
          onCancel={() => {
            setEditingSenderId(undefined);
            pop();
          }}
          onSubmit={() => {
            setEditingSenderId(undefined);
            setSenderIdReloadKey(Date.now());
            setTrademarkVerificationReloadKey(Date.now());
            pop();
          }}
        />
      )}
      {renderForm(
        ModalType.CompanyDetails,
        <BrandDetailsForm
          brand={brand}
          proceeding={updating}
          onCancel={pop}
          onSubmit={handleEditSubmit}
        />
      )}
    </>
  );
};

export default BrandDetails;
